import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getNews } from "../httpService.js";
import { getSnapshotValue } from "../snapshot.js";
import { UserContext } from "./UserContext.jsx";

const NewsContext = React.createContext({});

// getNews() hands out Date objects; the snapshot is JSON, so restore that shape here —
// consumers call article.createdAt.toLocaleDateString() directly.
const withDates = (articles) =>
  articles.map((article) => ({
    ...article,
    createdAt: new Date(article.createdAt),
    updatedAt: new Date(article.updatedAt),
  }));

const NewsContextProvider = (props) => {
  const [news, setNews] = useState(() => withDates(getSnapshotValue("news", [])));

  const { hasRole, initialized, locale } = useContext(UserContext);
  const adminData = hasRole("admin");

  const initializeData = useCallback(async () => {
    try {
      const dataLocale = adminData ? "sv" : locale;
      const news = await getNews(dataLocale);
      setNews(news);
    } catch {
      // News fetch failed silently — page still renders with defaults
    }
  }, [adminData, locale]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adminData intentionally triggers re-fetch for admin data
  useEffect(() => {
    if (initialized) {
      initializeData();
    }
  }, [initialized, adminData, initializeData]);

  const value = useMemo(
    () => ({
      news,
      reinitialize: initializeData,
    }),
    [news, initializeData]
  );

  return <NewsContext.Provider value={value} {...props} />;
};

export { NewsContextProvider, NewsContext };
